import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AdCard from "../components/AdCard";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Search, X, Filter, MapPin, Store, TrendingUp } from "lucide-react";

export default function Directories() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab State: "comercios" or "cps"
  const [activeTab, setActiveTab] = useState("comercios");

  // Postal codes list state (original view)
  const [cpList, setCpList] = useState([]);
  const [loadingCPs, setLoadingCPs] = useState(true);

  // Search/Filters state (general view)
  const giro = params.get("giro") || "";
  const initialQ = params.get("q") || "";
  const [qInput, setQInput] = useState(initialQ);
  const [q, setQ] = useState(initialQ);
  const [results, setResults] = useState([]);
  const [giros, setGiros] = useState([]);
  const [loadingResults, setLoadingResults] = useState(true);

  // Debounce typing into the search query
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Sync `q` to URL
  useEffect(() => {
    const currentQ = params.get("q") || "";
    if (q === currentQ) return;
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Fetch all active businesses with filters
  useEffect(() => {
    let cancelled = false;
    setLoadingResults(true);
    const search = new URLSearchParams();
    if (giro) search.set("giro", giro);
    if (q) search.set("q", q);

    api.get(`/directory/all?${search.toString()}`)
      .then((r) => {
        if (!cancelled) {
          setResults(r.data.results || []);
          setGiros(r.data.giros_available || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setGiros([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingResults(false);
      });

    return () => {
      cancelled = true;
    };
  }, [giro, q]);

  // Fetch postal codes list (original functionality)
  useEffect(() => {
    api.get("/directory/list")
      .then((r) => setCpList(r.data || []))
      .finally(() => setLoadingCPs(false));
  }, []);

  const setGiro = (g) => {
    const next = new URLSearchParams(params);
    if (g) next.set("giro", g);
    else next.delete("giro");
    if (q) next.set("q", q);
    setParams(next, { replace: true });
  };

  const hasFilters = !!(giro || q);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="overline text-[hsl(var(--primary))]">Directorio General</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tight">
          Explora los comercios y ofertas
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Encuentra promociones reales, productos y servicios de todos los comercios registrados en la plataforma.
        </p>

        {/* Navigation Tabs */}
        <div className="flex border-b border-border mt-8 mb-8" data-testid="directory-tabs">
          <button
            onClick={() => setActiveTab("comercios")}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 font-display text-lg font-medium transition-all ${
              activeTab === "comercios"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-muted-foreground hover:text-zinc-700"
            }`}
            data-testid="tab-comercios"
          >
            <Store className="w-5 h-5" />
            Todos los comercios
          </button>
          <button
            onClick={() => setActiveTab("cps")}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 font-display text-lg font-medium transition-all ${
              activeTab === "cps"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-muted-foreground hover:text-zinc-700"
            }`}
            data-testid="tab-cps"
          >
            <MapPin className="w-5 h-5" />
            Ver por código postal
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "comercios" ? (
          <div>
            {/* Search Input */}
            <div className="relative mb-6" data-testid="general-search-section">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-zinc-400" />
              </div>
              <input
                type="search"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Buscar por nombre, categoría, producto o promoción (ej. tacos, 2x1, estética…)"
                className="w-full pl-12 pr-12 py-4 rounded-2xl border border-border bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40 focus:border-[#7C3AED] text-sm md:text-base"
                data-testid="general-search-input"
              />
              {qInput && (
                <button
                  type="button"
                  onClick={() => setQInput("")}
                  className="absolute inset-y-0 right-4 flex items-center text-zinc-400 hover:text-zinc-700"
                  data-testid="general-search-clear"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Category Chips */}
            {giros.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="giro-filter-section">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mr-1">
                  <Filter className="w-3.5 h-3.5" /> Filtrar por giro:
                </div>
                <button
                  onClick={() => setGiro("")}
                  className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${
                    !giro
                      ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                      : "bg-white border-border hover:border-[#C4B5FD] hover:bg-[#F5F3FF] text-zinc-700"
                  }`}
                  data-testid="giro-chip-all"
                >
                  Todos
                </button>
                {giros.map((g) => {
                  const active = giro.toLowerCase() === g.toLowerCase();
                  return (
                    <button
                      key={g}
                      onClick={() => setGiro(active ? "" : g)}
                      className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${
                        active
                          ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                          : "bg-white border-border hover:border-[#C4B5FD] hover:bg-[#F5F3FF] text-zinc-700"
                      }`}
                      data-testid={`giro-chip-${g.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Results count / Sort indicator */}
            {!loadingResults && results.length > 0 && (
              <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground bg-white border border-border rounded-full px-3 py-1 mb-6" data-testid="sort-indicator">
                <TrendingUp className="w-3 h-3 text-emerald-600" />
                Ordenados por mejor promoción primero · {results.length} comercio{results.length === 1 ? "" : "s"}
              </div>
            )}

            {/* Results Grid */}
            {loadingResults ? (
              <div className="text-muted-foreground" data-testid="general-directory-loading">Cargando comercios…</div>
            ) : results.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger" data-testid="general-directory-results">
                {results.map((b, i) => (
                  <AdCard key={b.id} business={b} ad={b.ad} index={i} />
                ))}
              </div>
            ) : hasFilters ? (
              <div className="bg-white border border-border rounded-2xl p-12 text-center" data-testid="general-directory-empty-filter">
                <div className="font-display text-2xl">Sin resultados</div>
                <p className="text-muted-foreground mt-2">
                  {q && <>No encontramos comercios que coincidan con "<b>{q}</b>"{giro ? <> en el giro <b>{giro}</b></> : ""}.</>}
                  {!q && giro && <>No hay comercios en el giro "<b>{giro}</b>" actualmente.</>}
                </p>
                <div className="flex gap-2 justify-center mt-5">
                  {q && (
                    <button onClick={() => setQInput("")} className="rounded-full px-5 py-2.5 border border-border bg-white hover:bg-muted text-sm" data-testid="clear-q-btn">
                      Limpiar búsqueda
                    </button>
                  )}
                  {giro && (
                    <button onClick={() => setGiro("")} className="btn-primary inline-block rounded-full px-5 py-2.5 text-sm" data-testid="clear-giro-btn">
                      Ver todos los giros
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-border rounded-2xl p-12 text-center" data-testid="general-directory-empty">
                <div className="font-display text-2xl">Aún no hay comercios activos</div>
                <p className="text-muted-foreground mt-2">Sé el primero en afiliar tu negocio.</p>
                <Link to="/registro" className="btn-primary inline-block mt-5 rounded-full px-6 py-3 font-medium">
                  Registrar mi comercio
                </Link>
              </div>
            )}
          </div>
        ) : (
          /* Postal Codes view (Original page layout) */
          <div>
            {loadingCPs ? (
              <div className="text-muted-foreground">Cargando códigos postales…</div>
            ) : cpList.length === 0 ? (
              <div className="bg-white border border-border rounded-2xl p-10 text-center">
                <div className="font-display text-2xl">Aún no hay directorios activos</div>
                <p className="text-muted-foreground mt-2">Sé parte del primero. Registra tu comercio hoy.</p>
                <Link to="/registro" className="btn-primary inline-block mt-5 rounded-full px-6 py-3 font-medium">
                  Registrar comercio
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger" data-testid="directories-grid">
                {cpList.map((d) => (
                  <button
                    key={d.postal_code}
                    onClick={() => navigate(`/directorio/${d.postal_code}`)}
                    className="text-left bg-white border border-border rounded-2xl p-5 hover:border-[hsl(var(--primary))] hover:shadow-md transition-all"
                    data-testid={`dir-card-${d.postal_code}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-sm text-muted-foreground">CP {d.postal_code}</div>
                      <ArrowRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                    {d.name ? (
                      <div className="font-display text-lg mt-1 text-[#5B21B6] leading-tight">{d.name}</div>
                    ) : (
                      <div className="font-display text-lg mt-1 leading-tight">Directorio CP {d.postal_code}</div>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">{d.colonia || "—"}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{d.city || ""} {d.state ? `· ${d.state}` : ""}</div>
                    <div className="mt-4 overline text-[#7C3AED]">{d.count} comercios activos</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
